import { Card } from "@repo/ui/components/ui/card";
import { cn } from "@repo/ui/lib/utils";
import { Building2, Check, Users, PartyPopper } from "lucide-react";
import { useState } from "react";
import { StepDone } from "./steps/step-done";
import { StepGroupe } from "./steps/step-groupe";
import { StepUtilisateurs } from "./steps/step-utilisateurs";

// ─── Types ────────────────────────────────────────────────────────────────────

type WizardStep = "groupe" | "utilisateurs" | "done";

interface CreatedGroupData {
  id: number;
  name: string;
  groupe_type: string;
  licence: number;
  plan_type?: string;
  max_parcours_per_month?: number;
  tier_id?: string;
}

interface CreatedUserData {
  id: number;
  username: string;
  email: string;
  profil_name?: string;
}

// ─── Step metadata ────────────────────────────────────────────────────────────

const STEPS: {
  id: WizardStep;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  description: string;
}[] = [
  {
    id: "groupe",
    label: "Groupe",
    shortLabel: "1",
    icon: Building2,
    description: "Nom, type et plan",
  },
  {
    id: "utilisateurs",
    label: "Utilisateurs",
    shortLabel: "2",
    icon: Users,
    description: "Comptes & accès",
  },
  {
    id: "done",
    label: "Terminé",
    shortLabel: "3",
    icon: PartyPopper,
    description: "Récapitulatif",
  },
];

const STEP_ORDER: WizardStep[] = ["groupe", "utilisateurs", "done"];

// ─── Wizard stepper UI ────────────────────────────────────────────────────────

interface StepperProps {
  current: WizardStep;
}

function Stepper({ current }: StepperProps) {
  const currentIndex = STEP_ORDER.indexOf(current);

  return (
    <div className="flex items-center justify-between w-full" aria-label="Étapes de création">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const status =
          index < currentIndex
            ? "completed"
            : index === currentIndex
              ? "active"
              : "pending";

        return (
          <div key={step.id} className="flex items-center flex-1">
            {/* Step node */}
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              <div
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all duration-300",
                  status === "completed" &&
                    "border-primary bg-primary text-primary-foreground shadow-sm shadow-primary/30",
                  status === "active" &&
                    "border-primary bg-primary/10 text-primary shadow-sm shadow-primary/20",
                  status === "pending" &&
                    "border-border bg-muted text-muted-foreground"
                )}
              >
                {status === "completed" ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </div>
              <div className="text-center hidden sm:block">
                <p
                  className={cn(
                    "text-xs font-semibold leading-none",
                    status === "active" ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  {step.label}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {step.description}
                </p>
              </div>
            </div>

            {/* Connector line (not after last step) */}
            {index < STEPS.length - 1 && (
              <div className="flex-1 mx-2 sm:mx-3 mt-0 sm:-mt-5">
                <div
                  className={cn(
                    "h-0.5 w-full rounded-full transition-all duration-500",
                    index < currentIndex ? "bg-primary" : "bg-border/60"
                  )}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step title section ───────────────────────────────────────────────────────

interface StepHeaderProps {
  step: WizardStep;
}

const STEP_COPY: Record<
  WizardStep,
  { title: string; subtitle: string }
> = {
  groupe: {
    title: "Créer le groupe",
    subtitle:
      "Définissez les informations du groupe, son type et son forfait si nécessaire.",
  },
  utilisateurs: {
    title: "Ajouter des utilisateurs",
    subtitle:
      "Ajoutez un ou plusieurs utilisateurs à rattacher à ce groupe. Vous pouvez passer cette étape.",
  },
  done: {
    title: "Tout est prêt",
    subtitle: "Récapitulatif de ce qui a été créé.",
  },
};

function StepHeader({ step }: StepHeaderProps) {
  const copy = STEP_COPY[step];
  return (
    <div className="space-y-1">
      <h2 className="text-xl font-bold text-foreground">{copy.title}</h2>
      <p className="text-sm text-muted-foreground">{copy.subtitle}</p>
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

export function CreationRapide() {
  const [step, setStep] = useState<WizardStep>("groupe");
  const [createdGroup, setCreatedGroup] = useState<CreatedGroupData | null>(null);
  const [createdUsers, setCreatedUsers] = useState<CreatedUserData[]>([]);

  const handleGroupeCreated = (group: CreatedGroupData) => {
    setCreatedGroup(group);
    setStep("utilisateurs");
  };

  const handleUsersCreated = (users: CreatedUserData[]) => {
    setCreatedUsers(users);
    setStep("done");
  };

  const handleSkipUsers = () => {
    setCreatedUsers([]);
    setStep("done");
  };

  const handleReset = () => {
    setStep("groupe");
    setCreatedGroup(null);
    setCreatedUsers([]);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-foreground">
          Création rapide
        </h1>
        <p className="text-muted-foreground text-sm">
          Créez un groupe et ses utilisateurs en une seule fois, sans changer de page.
        </p>
      </div>

      {/* Wizard card */}
      <Card className="overflow-hidden shadow-sm border-border/60">
        {/* Stepper — sits in a visually separated top band */}
        <div className="border-b border-border/40 bg-muted/30 px-6 py-4">
          <Stepper current={step} />
        </div>

        {/* Step content */}
        <div className="px-6 py-6 space-y-6">
          <StepHeader step={step} />

          <div className="min-h-[24rem]">
            {step === "groupe" && (
              <StepGroupe onGroupeCreated={handleGroupeCreated} />
            )}

            {step === "utilisateurs" && createdGroup && (
              <StepUtilisateurs
                createdGroup={createdGroup}
                onUsersCreated={handleUsersCreated}
                onSkip={handleSkipUsers}
              />
            )}

            {step === "done" && createdGroup && (
              <StepDone
                createdGroup={createdGroup}
                createdUsers={createdUsers}
                onReset={handleReset}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
